import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Mail, Shield, MoreVertical, Pencil, Trash2, UserX, UserCheck, Key } from 'lucide-react';

const Users = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ nome: '', email: '', role: 'Funcionário' });
  const [editingUser, setEditingUser] = useState(null);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });

  useEffect(() => {
    fetchUsers();
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, { ...formData, password: 'temp123' });
      toast.success('Usuário criado! Email de ativação enviado.');
      setDialogOpen(false);
      setFormData({ nome: '', email: '', role: 'Funcionário' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setFormData({ nome: u.nome, email: u.email, role: u.role });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${editingUser.id}`, { ...formData, password: 'temp' });
      toast.success('Usuário atualizado!');
      setEditDialogOpen(false);
      setEditingUser(null);
      setFormData({ nome: '', email: '', role: 'Funcionário' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar usuário');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/users/${userId}/toggle-status`);
      toast.success(`Usuário ${currentStatus ? 'inativado' : 'ativado'}!`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Deseja realmente excluir este usuário?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Usuário excluído!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const handlePasswordChange = (u) => {
    setEditingUser(u);
    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    setPasswordDialogOpen(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    try {
      const payload = {
        new_password: passwordData.new_password
      };
      
      // If changing own password and is employee, include current password
      if (user.role === 'Funcionário' && editingUser.id === user.id) {
        payload.current_password = passwordData.current_password;
      }

      await axios.put(`${API}/users/${editingUser.id}/change-password`, payload);
      toast.success('Senha alterada com sucesso!');
      setPasswordDialogOpen(false);
      setEditingUser(null);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  const isAdmin = user?.role === 'Admin';
  const currentUser = users.find(u => u.id === user?.id);
  const displayUsers = isAdmin ? users : (currentUser ? [currentUser] : []);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-500 bg-clip-text text-transparent mb-2">
            Usuários
          </h1>
          <p className="text-gray-600">{isAdmin ? 'Gerencie usuários da empresa' : 'Meu perfil'}</p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-pink-500 to-rose-400 hover:from-pink-600 hover:to-rose-500" data-testid="add-user-button">
                <Plus className="mr-2 h-4 w-4" />
                Novo Usuário
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="user-dialog">
              <DialogHeader>
                <DialogTitle>Novo Usuário</DialogTitle>
                <DialogDescription>Usuário receberá email com senha temporária</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Perfil</Label>
                  <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Funcionário">Funcionário</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-pink-500 to-rose-400" data-testid="user-submit-button">
                  Criar Usuário
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
            <DialogDescription>
              {isAdmin ? 'Atualize as informações do usuário' : 'Você pode alterar apenas seu nome'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nome">Nome</Label>
              <Input
                id="edit-nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
                data-testid="edit-user-name-input"
              />
            </div>
            {isAdmin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="edit-user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Perfil</Label>
                  <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
                    <SelectTrigger data-testid="edit-user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Funcionário">Funcionário</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <Button type="submit" className="w-full bg-gradient-to-r from-pink-500 to-rose-400" data-testid="edit-user-submit-button">
              Atualizar
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle>Alterar Senha</DialogTitle>
            <DialogDescription>
              {isAdmin && editingUser?.id !== user?.id
                ? 'Redefina a senha do usuário'
                : 'Digite sua senha atual e a nova senha'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {(!isAdmin || editingUser?.id === user?.id) && (
              <div className="space-y-2">
                <Label htmlFor="current-password">Senha Atual</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  data-testid="current-password-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova Senha</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar Nova Senha</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-pink-500 to-rose-400" data-testid="password-submit-button">
              Alterar Senha
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {displayUsers.length === 0 ? (
        <Card className="border-pink-100 bg-white/80 backdrop-blur-sm" data-testid="no-users">
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Nenhum usuário cadastrado ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayUsers.map((u) => {
            const isCurrentUser = u.id === user?.id;
            return (
              <Card
                key={u.id}
                className={`border-pink-100 bg-white/80 backdrop-blur-sm shadow-md hover:shadow-xl transition-shadow ${
                  isCurrentUser ? 'ring-2 ring-pink-300' : ''
                }`}
                data-testid={`user-card-${u.id}`}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl flex items-center gap-2">
                        {u.nome}
                        {isCurrentUser && (
                          <Badge variant="outline" className="text-xs">
                            Você
                          </Badge>
                        )}
                      </CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={u.role === 'Admin' ? 'default' : 'secondary'} className="text-xs">
                        <Shield className="w-3 h-3 mr-1" />
                        {u.role}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`user-actions-${u.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(u)} data-testid={`edit-user-${u.id}`}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePasswordChange(u)} data-testid={`change-password-${u.id}`}>
                            <Key className="mr-2 h-4 w-4" />
                            Alterar Senha
                          </DropdownMenuItem>
                          {isAdmin && !isCurrentUser && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleToggleStatus(u.id, u.ativo)}
                                data-testid={`toggle-status-${u.id}`}
                              >
                                {u.ativo ? (
                                  <>
                                    <UserX className="mr-2 h-4 w-4" />
                                    Inativar
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="mr-2 h-4 w-4" />
                                    Ativar
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(u.id)}
                                className="text-red-600"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Excluir
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span>{u.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={u.ativo ? 'default' : 'outline'} className="text-xs">
                      {u.ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Users;
